import { Fragment, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import Image from 'components/Common/Image'
import FlightCartReviewTileExtension from 'components/Pages/CartReviewPage/FlightCartReviewTileExtension'
import FareFamilyFlightTileV2TimeLine from 'components/Flights/V2/FareFamily/FareFamilyFlightTileV2TimeLine'
import { INTL_DMY_CASUAL_FORMAT, INTL_SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import CartPanelFlightLegExtension from './CartPanelFlightLegExtension'
import {
  cartPanelDesktopDividerPaddingStyles,
  cartPanelDesktopLegPaddingStyles,
  cartPanelTripCartDividerPaddingStyles,
  cartPanelTripCartLegPaddingStyles,
} from './cartPanelLayout.styles'

const TripCartDividerPadding = styled.div`
  ${cartPanelTripCartDividerPaddingStyles}
`

const DesktopDividerPadding = styled.div`
  ${cartPanelDesktopDividerPaddingStyles}
`

const TripCartProjectedLegContainer = styled(Group)`
  ${cartPanelTripCartLegPaddingStyles}
`

const DesktopProjectedLegContainer = styled(Group)`
  ${cartPanelDesktopLegPaddingStyles}
`

const ProjectedLegPrimaryContent = styled.div`
  flex-grow: 1;
`

const AirlineLogoContainer = styled.div`
  width: ${rem(120)};
  height: ${rem(52)};
`

const AirlineLogoImage = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

interface Props {
  flights: ReadonlyArray<App.Checkout.FlightItem['flights'][number]>
  isClassicReward: boolean
  hideFareDetails?: boolean
  useTripCartPadding?: boolean
}

interface ProjectedLegProps {
  flight: App.Checkout.FlightItemFlight
  useTripCartPadding: boolean
}

function getFlightExtensionKey(flight: App.Checkout.FlightItem['flights'][number], legNumber: number): string {
  return [
    `leg-${legNumber}`,
    flight.journeyId,
    flight.journeyKey,
    flight.rewardJourneyKey,
    flight.carrier,
    flight.departingAirportCode,
    flight.arrivalAirportCode,
    flight.departureDateTime,
    flight.arrivalDateTime,
  ].filter(Boolean).join('|')
}

function CartPanelFlightProjectedLegExtension({ flight, useTripCartPadding }: ProjectedLegProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const formattedTimes = useMemo(() => ({
    departureTime: flight.departureDateTime
      ? dateFormatter(flight.departureDateTime, INTL_SHORT_TIME_FORMAT_24_HOUR)
      : undefined,
    arrivalTime: flight.arrivalDateTime
      ? dateFormatter(flight.arrivalDateTime, INTL_SHORT_TIME_FORMAT_24_HOUR)
      : undefined,
    departureDate: dateFormatter(flight.departureDateTime, INTL_DMY_CASUAL_FORMAT),
    arrivalDate: dateFormatter(flight.arrivalDateTime, INTL_DMY_CASUAL_FORMAT),
    duration: flight.duration ? intl.formatMessage(
      {
        id: 'bGkrZgVl6IGhP-q1n1I7',
        defaultMessage: '{hours}h {minutes}m',
        description: 'Flight duration in hours and minutes for the cart review flight tile',
      },
      { hours: flight.duration.hours, minutes: flight.duration.minutes },
    ) : undefined,
  }), [dateFormatter, flight.arrivalDateTime, flight.departureDateTime, flight.duration, intl])

  const LegContainer = useTripCartPadding ? TripCartProjectedLegContainer : DesktopProjectedLegContainer

  return (
    <LegContainer direction="vertical" tabletDirection="horizontal" gap={16} desktopGap={32} tabletWrap="wrap">
      <ProjectedLegPrimaryContent>
        <Group direction="horizontal" gap={16} desktopGap={48} verticalAlign="center">
          <AirlineLogoContainer>
            <AirlineLogoImage
              width={40}
              image={flight.carrierImage}
            />
          </AirlineLogoContainer>

          <FareFamilyFlightTileV2TimeLine
            departureTime={formattedTimes.departureTime}
            arrivalTime={formattedTimes.arrivalTime}
            arrivalDate={formattedTimes.arrivalDate}
            departureDate={formattedTimes.departureDate}
            departureAirport={flight.departingAirportCode}
            arrivalAiport={flight.arrivalAirportCode}
            duration={formattedTimes.duration}
            stops={flight.numberOfStops || 0}
          />
        </Group>
      </ProjectedLegPrimaryContent>
    </LegContainer>
  )
}

interface FlightLegExtensionProps {
  flight: App.Checkout.FlightItemFlight
  isClassicReward: boolean
  hideFareDetails: boolean | undefined
  useTripCartPadding: boolean
}

function CartPanelFlightLegExtensionItem({
  flight,
  isClassicReward,
  hideFareDetails,
  useTripCartPadding,
}: FlightLegExtensionProps) {
  if (hideFareDetails) {
    return (
      <CartPanelFlightProjectedLegExtension
        flight={flight}
        useTripCartPadding={useTripCartPadding}
      />
    )
  }

  if (useTripCartPadding) {
    return (
      <CartPanelFlightLegExtension
        flight={flight}
        isClassicReward={isClassicReward}
      />
    )
  }

  return (
    <FlightCartReviewTileExtension
      flight={flight}
      isClassicReward={isClassicReward}
    />
  )
}

function CartPanelFlightLegExtensions({
  flights,
  isClassicReward,
  hideFareDetails,
  useTripCartPadding = false,
}: Props) {
  const DividerPadding = useTripCartPadding ? TripCartDividerPadding : DesktopDividerPadding

  return (
    <Group direction="vertical" gap={0}>
      <DividerPadding><Divider kind="primary" /></DividerPadding>
      {flights.map((flight, index) => (
        <Fragment key={getFlightExtensionKey(flight, index + 1)}>
          <CartPanelFlightLegExtensionItem
            flight={flight}
            isClassicReward={isClassicReward}
            hideFareDetails={hideFareDetails}
            useTripCartPadding={useTripCartPadding}
          />
          {index < flights.length - 1 && (
            <DividerPadding><Divider kind="primary" /></DividerPadding>
          )}
        </Fragment>
      ))}
    </Group>
  )
}

export default CartPanelFlightLegExtensions
